import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Stack, Text, Image } from '@chakra-ui/react';
import { FinancialInsightSubHeader } from './financialInsightSubHeader';
import LocationAnalysisTable from './locationAnalysisTable';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { SectionHeader } from '../sectionHeader/SectionHeader';
import LocationAnalysisImage from '../../media/images/LocationAnalysisImage.png';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import {
  extractIntegrationIds,
  extractLocationIds,
} from '../../utils/integrationUtils';
import { useSelector } from 'react-redux';

const parseValue = value => {
  if (value === null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    // Remove % and any other non-numeric characters except decimal point and minus sign
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return 0;
};

const LocationAnalysis = ({
  setLocation,
  locationValue,
  fromDate,
  toDate,
  periodFrom,
  periodTo,
  periodSelect,
  dateValue,
  value,
}) => {
  const locationData = useSelector(
    state => state.locationSelectFormat.locationData
  );

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('cost_amt');
  const [nameType, setNameType] = useState('Overview');

  // Use props if provided, otherwise use default date range (last 365 days / 1 year)
  // This matches the date selector's "All time" default
  // Memoize default dates to avoid recalculation on every render
  const defaultDates = useMemo(() => {
    const today = new Date();
    const toDate = today
      .toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');

    const fromDateObj = new Date(today);
    fromDateObj.setDate(today.getDate() - 365); // Last 365 days (1 year)
    const fromDate = fromDateObj
      .toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
      .replace(/\//g, '-');

    return { fromDate, toDate };
  }, []); // Empty dependency array - only calculate once

  // Memoize effective dates to avoid recalculation and dependency issues
  const effectiveFromDate = useMemo(
    () => fromDate || defaultDates.fromDate,
    [fromDate, defaultDates.fromDate]
  );
  const effectiveToDate = useMemo(
    () => toDate || defaultDates.toDate,
    [toDate, defaultDates.toDate]
  );

  // Use useCallback to ensure all dependencies are properly tracked for API calls
  const fetchData = useCallback(async () => {
    if (locationValue?.length !== 0) {
      setLoading(true);

      // Extract integration IDs from selected locations (full integrations only)
      const integrationIds = extractIntegrationIds(locationValue, locationData);

      // Extract location IDs from selected locations (individual locations only)
      const locationIds = extractLocationIds(locationValue, locationData);

      // Only error if we have neither integration IDs nor location IDs
      if (integrationIds.length === 0 && locationIds.length === 0) {
        const selectedLocationValues = Array.isArray(locationValue)
          ? locationValue
          : [locationValue];
        console.error(
          'No valid integration IDs or location IDs found for selected locations:',
          { selectedLocationValues, integrationIds, locationIds }
        );
        setLoading(false);
        return;
      }

      // Format dates to MM-DD-YYYY before sending to API
      const formattedFromDate = periodSelect
        ? ''
        : formatDateToMMDDYYYY(effectiveFromDate);
      const formattedToDate = periodSelect
        ? ''
        : formatDateToMMDDYYYY(effectiveToDate);

      // Validate that we have either valid dates or valid periods
      if (!periodSelect && (!formattedFromDate || !formattedToDate)) {
        console.error('No valid date range provided:', {
          formattedFromDate,
          formattedToDate,
          effectiveFromDate,
          effectiveToDate,
        });
        setLoading(false);
        return;
      }

      if (periodSelect && (!periodFrom || !periodTo)) {
        console.error('No valid period range provided:', {
          periodFrom,
          periodTo,
        });
        setLoading(false);
        return;
      }

      const body = new FormData();
      // Only send integration_ids if user selected integrations
      if (integrationIds.length > 0) {
        body.append('integration_ids', integrationIds.join(','));
      }
      // Only send location_ids if user selected locations
      if (locationIds.length > 0) {
        body.append('location_ids', locationIds.join(','));
      }

      body.append('fromDate', formattedFromDate);
      body.append('toDate', formattedToDate);
      body.append('type', type);
      body.append('periodFrom', periodSelect ? periodFrom : '');
      body.append('periodTo', periodSelect ? periodTo : '');

      try {
        const response = await fetchWithAuth(
          '/api/ddl_value_generator_multiselect/',
          {
            method: 'POST',
            body: body,
          }
        );

        const result = await response.json();
        if (result.code === undefined) {
          setData(result);
        } else {
          window.open('/', '_self');
          alert('Session Expired!.');
        }
      } catch (err) {
        console.error('Error fetching location analysis data:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [
    locationValue,
    periodSelect,
    fromDate,
    toDate,
    periodFrom,
    periodTo,
    type,
    locationData,
    defaultDates.fromDate,
    defaultDates.toDate,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDownload = () => {
    if (!data || !data.length) return;

    // Prepare headers
    const headers = ['Description', 'Classification', ...columns];

    // Helper function to escape CSV values
    const escapeCSV = value => {
      // If the value contains commas, quotes, or newlines, wrap it in quotes
      // Also escape any existing quotes by doubling them
      if (
        value &&
        (value.includes(',') || value.includes('"') || value.includes('\n'))
      ) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Prepare data rows
    const rows = data.map(item => {
      const row = [
        escapeCSV(item.desc || ''),
        escapeCSV(item.classification || ''),
        ...columns.map(column => {
          const value = item[column];
          // Format the value based on the type and escape for CSV
          if (type === 'cost_amt') {
            return value
              ? escapeCSV(`$${parseValue(value).toLocaleString()}`)
              : '';
          } else {
            return value ? escapeCSV(`${parseValue(value).toFixed(2)}%`) : '';
          }
        }),
      ];
      return row;
    });

    // Create CSV content
    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `location_analysis_${type}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleViewTypeChange = (_event, newType) => {
    setType(newType);
    switch (newType) {
      case 'cost_amt':
        setNameType('Overview');
        break;
      case 'cost_per':
        setNameType('% of cost');
        break;
      case 'sales_per':
        setNameType('% of sales');
        break;
      default:
        setNameType('Overview');
    }
  };

  // Get unique location names from the data
  const columns =
    data.length > 0
      ? [
          ...new Set(
            data.flatMap(item =>
              Object.keys(item).filter(
                key => key !== 'desc' && key !== 'classification'
              )
            )
          ),
        ]
      : [];

  return (
    <Box>
      {/* Placeholder for Section Header */}
      <Box mb={4}>
        <SectionHeader
          title="Location Analysis"
          showDateSelector={true}
          dateValue={dateValue}
          value={value}
        />
      </Box>

      <Stack spacing={4}>
        <FinancialInsightSubHeader
          locationValue={locationValue}
          setLocation={setLocation}
          setViewType={handleViewTypeChange}
          viewType={nameType}
          handleDownload={handleDownload}
        />
        {locationValue?.length > 0 ? (
          <LocationAnalysisTable
            data={data}
            columns={columns}
            loading={loading}
            type={type}
          />
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minH="calc(100vh - 500px)" // Adjust this value based on your header height
            w="100%"
            p={4}
          >
            <Image
              src={LocationAnalysisImage}
              alt="Location Analysis - Select a location"
              mb={16}
            />
            <Text fontSize={18} fontWeight={600}>
              Select a location
            </Text>
            <Text
              fontSize={16}
              fontWeight={400}
              maxW={320}
              textAlign={'center'}
            >
              Select one or more locations to generate a detailed analysis.
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

// const mapStateToProps = state => ({
//   dataLoading: state.dataFetch.dataLoading,
// });

// const mapDispatchToProps = {
//   setDataLoading,
// };

export default LocationAnalysis;
